import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, distinctUntilChanged, finalize, forkJoin, map, Observable, of, take } from 'rxjs';
import { CorrelationsPathApiService } from '@app/api/services/correlation-path-api.service';
import { selectMainProjectId } from '@app/main/main-screen/store/main-selection/main-shared-selection/main-shared-selection.selectors';
import { Store } from '@ngrx/store';
import { filter, shareReplay, switchMap, tap } from 'rxjs/operators';
import { ICorrelationPath } from '@app/api/interfaces/correlation-path.interface';

@Injectable({ providedIn: 'root' })
export class CorrelationPathsService {
  private correlationsPathApiService = inject(CorrelationsPathApiService);
  private store = inject(Store);

  private cache = new Map<string, ICorrelationPath>();

  private loadingCounter$ = new BehaviorSubject(0);
  public loading$ = this.loadingCounter$.pipe(
    map(v => v > 0),
    distinctUntilChanged()
  );

  private request$ = new BehaviorSubject<string[] | null>(null);

  public getCorrelationPathsMap(correlationIds: string[]): Observable<Record<string, ICorrelationPath>> {
    this.request$.next(correlationIds);
    return this.result$.pipe(
      filter(res => correlationIds.every(id => id in res)),
      take(1)
    );
  }

  public clearCache() {
    this.cache.clear();
  }

  private result$ = this.request$.pipe(
    filter((ids): ids is string[] => !!ids),
    switchMap(correlationIds =>
      this.store.select(selectMainProjectId).pipe(
        take(1),
        filter((projectId): projectId is string => !!projectId),
        switchMap(projectId => {
          const toLoad = correlationIds.filter(id => !this.cache.has(id));

          const buildResult = (): Record<string, ICorrelationPath> =>
            correlationIds.reduce<Record<string, ICorrelationPath>>((acc, id) => {
              const path = this.cache.get(id);
              if (path) acc[id] = path;
              return acc;
            }, {});

          if (!toLoad.length) {
            return of(buildResult());
          }

          this.loadingCounter$.next(this.loadingCounter$.value + 1);

          return forkJoin(
            toLoad.map(id => this.correlationsPathApiService.getOne(projectId, id).pipe(map(res => res.data)))
          ).pipe(
            tap(paths => paths.forEach((path, index) => this.cache.set(toLoad[index], path))),
            map(buildResult),
            catchError(err => {
              console.error('Failed to load correlation paths', err);
              return of(buildResult());
            }),
            finalize(() => this.loadingCounter$.next(this.loadingCounter$.value - 1))
          );
        })
      )
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );
}
